import { LoadState } from "../../constants/enums";
import {
  EnrolmentStatus,
  SequenceModule,
  SequenceStatus,
  SequenceTriggerType,
} from "../../views/emailSequences/emailSequences.labels";

/* ------------------------------------------------------------------ *
 * Condition node shape reused from the workflow condition builder.
 * (PRD §5.2.B — produces { and: [...], or: [...] } groups.)
 * ------------------------------------------------------------------ */
export type ConditionOperant =
  | "EQUAL"
  | "NOT_EQUAL"
  | "GREATER"
  | "LESSER"
  | "GREATER_THAN_EQUAL"
  | "LESSER_THAN_EQUAL";

export interface ISequenceConditionRow {
  column: string;
  operant: ConditionOperant | "";
  columnOrValue: string;
  isColumnComparison?: boolean;
}

/** Each group is keyed by "and" or "or" and holds a list of rows. */
export type ISequenceConditionGroup = {
  [key in "and" | "or"]?: ISequenceConditionRow[];
};

/** The serialised exit/skip condition node (nested and/or). */
export type ISequenceConditionNode = ISequenceConditionGroup;

/* ------------------------------------------------------------------ *
 * Scheduled rule (CRON_SQL) schedule.
 *
 * This is the exact shape the server expects in `cron_schedule_at` on
 * POST /api/v1/sequence/upsert-sequence (and returns on get-sequence).
 *
 * Rules enforced by the builder:
 *  - `second` is always "0". A missing second means "every second".
 *  - `minute` is required.
 *  - `hour` is required for everything except an hourly schedule.
 *  - `date` (1-31) is sent only for monthly schedules.
 *  - `month` (1-12) is sent only for yearly / specific-month schedules.
 *  - `week` (0-6, 0=Sunday, 1=Monday) is sent only to limit the weekdays.
 *  - "Every" is expressed by OMITTING the key — never "*" and never 0.
 *    (date:0 and month:0 are invalid; week:0 means Sunday only.)
 *
 * Times are interpreted in America/Toronto.
 * ------------------------------------------------------------------ */
export interface ISequenceCronSchedule {
  second?: string; // always "0" when present
  minute?: string; // 0-59
  hour?: string; // 0-23 (24-hour clock)
  date?: string; // 1-31 (day of month)
  month?: string; // 1-12
  week?: string; // 0-6 (0=Sunday, 1=Monday), supports "1-5" / "1,3,5"
}

/* ------------------------------------------------------------------ *
 * Send window (PRD §5.2.C)
 * ------------------------------------------------------------------ */
export interface ISequenceSendWindow {
  days: number[]; // ISO 1=Mon .. 7=Sun
  from: string; // "HH:mm"
  to: string; // "HH:mm"
  tz: string; // IANA name
}

/* ------------------------------------------------------------------ *
 * Steps (PRD §5.2.D)
 * ------------------------------------------------------------------ */
export type StepActionType = "EMAIL" | "TASK";
export type StepDelayType = "AFTER_PREVIOUS" | "DATE_FIELD";
export type StepToType = "CUSTOMER" | "PRODUCER" | "CSR" | "EMAIL_ADDRESS";
export type StepAssignToType = "CSR" | "PRODUCER" | "USER";

/**
 * "Only run when…" conditions (UI PRD R2 §1). Reuses the shared workflow
 * condition node shape: a single wrapper key (`and` = ALL, `or` = ANY) holding
 * a flat list of rows. `null` (or omitted) means no gating conditions.
 */
export type ISequenceRunConditions = ISequenceConditionNode;

export interface ISequenceSkipRule {
  rule: "template_sent" | "replied_since_last_step" | "record_condition";
  template_code?: string;
  since?: "enrollment" | "ever";
  conditions?: ISequenceConditionNode;
}

export interface ISequenceStep {
  step_order: number;
  step_name: string | null;
  action_type: StepActionType;
  // When
  delay_type: StepDelayType;
  delay_days: number;
  delay_hours: number;
  anchor_column: string | null;
  // Skip
  skip_conditions: ISequenceSkipRule[];
  // "Only run when…" conditions (UI PRD R2 §1). null = no gating.
  run_conditions: ISequenceRunConditions | null;
  // Email fields
  template_code: string | null;
  // Multi-select (UI PRD R2 §2). Older sequences may return a plain string;
  // normalise to an array on load.
  to_type: StepToType[] | null;
  to_emails: string[];
  cc_emails: string[];
  // Task fields
  task_name: string | null;
  task_type: string | null;
  task_priority: string | null;
  assign_to_type: StepAssignToType | null;
  assign_to_id: number | null;
  task_due_in_days: number | null;
  task_notes: string | null;
}

/* ------------------------------------------------------------------ *
 * The sequence itself (upsert payload + GET shape)
 * ------------------------------------------------------------------ */
export interface ISequence {
  sequence_code: string | null;
  sequence_name: string;
  description: string | null;
  // A DB view name resolved from the shared modules API (TableNameAutoComplete).
  // Historically one of SequenceModule (POLICY/CUSTOMER/LEAD); now any view name.
  module_name: string | null;
  trigger_type: SequenceTriggerType | null;
  trigger_template_code: string | null;
  // Contact-list sequences (UI PRD Round 3 §4). Required when trigger_type is
  // CONTACT_LIST; identifies the list whose contacts are enrolled.
  contact_list_code?: number | null;
  // Scheduled rule (CRON_SQL). The server field is `trigger_sql`.
  trigger_sql: string | null;
  cron_schedule_at: ISequenceCronSchedule | null;
  // Stop rules
  stop_on_reply: number; // 1/0
  stop_on_bounce: number; // 1/0
  allow_reentry: number; // 1/0
  exit_conditions: ISequenceConditionNode | null;
  // Send window
  send_window: ISequenceSendWindow | null;
  // Steps
  steps: ISequenceStep[];
  // Server-managed (read-only)
  status: SequenceStatus;
  version?: number;
  insert_ts?: string;
}

/** Row shape returned by get-sequence list. */
export interface ISequenceListRow {
  sequence_code: string;
  sequence_name: string;
  module_name: SequenceModule | string;
  trigger_type: SequenceTriggerType;
  status: SequenceStatus;
  version: number;
  insert_ts: string;
}

/* ------------------------------------------------------------------ *
 * Stats (PRD §5.3 Overview)
 * ------------------------------------------------------------------ */
export interface ISequenceStats {
  totals: {
    enrolled: number;
    ACTIVE: number;
    PAUSED: number;
    COMPLETED: number;
    STOPPED: number;
  };
  emails_sent: number;
  open_rate: number | null;
  reply_rate: number | null;
  // Server returns a map of { reason: count } (e.g. {} when empty). The array
  // form is kept for backward compatibility with older payloads.
  stop_reasons:
    | Record<string, number>
    | { reason: string; count: number }[];
  steps: ISequenceStatsStepRow[];
}

export interface ISequenceStatsStepRow {
  sequence_version: number;
  step_order: number;
  action_type: StepActionType;
  step_name?: string | null;
  sent: number;
  tasks: number;
  skipped: number;
  failed: number;
  opened: number;
  bounced: number;
}

/* ------------------------------------------------------------------ *
 * Enrolments (PRD §5.3 Enrolments tab & §5.5 record panel)
 * ------------------------------------------------------------------ */
export interface ISequenceEnrolment {
  enrollment_code: string;
  sequence_code: string;
  sequence_name?: string;
  module_name: SequenceModule | string;
  module_code: string;
  status: EnrolmentStatus;
  current_step_order: number;
  total_steps?: number;
  // Step progress (UI PRD R2 §3).
  completed_steps?: number;
  progress?: string; // e.g. "1/3"
  next_run_at: string | null;
  stop_reason: string | null;
  enrolled_source: string;
  enrolled_at: string;
  steps?: ISequenceEnrolmentStep[];
}

/**
 * Per-step lifecycle state in the enrolment checklist (UI PRD R2 §3).
 *  - DONE     the step ran (result carries SENT / TASK_CREATED / SKIPPED)
 *  - FAILED   the step errored
 *  - NEXT     the next step to run (has next_run_at)
 *  - PENDING  not run yet
 *  - NOT_RUN  will never run (sequence stopped / completed)
 */
export type EnrolmentStepState =
  | "DONE"
  | "FAILED"
  | "NEXT"
  | "PENDING"
  | "NOT_RUN";

export interface ISequenceEnrolmentStep {
  step_order: number;
  step_name: string | null;
  action_type: StepActionType;
  template_code?: string | null;
  task_name?: string | null;
  // Lifecycle state (UI PRD R2 §3). Older payloads may omit it; callers derive
  // a fallback from `result` / `executed_at`.
  state?: EnrolmentStepState | null;
  result: string | null; // SENT | TASK_CREATED | SKIPPED | FAILED | CLAIMED
  skip_reason?: string | null;
  executed_at: string | null;
  // Recipients of a sent mail (UI PRD R2 §3/§4).
  to_emails?: string[] | null;
  cc_emails?: string[] | null;
  // When the step is due (NEXT state).
  next_run_at?: string | null;
  // Whether the step carries "only run when…" conditions.
  has_run_conditions?: boolean;
  // Email delivery
  email_history_code?: string | null;
  delivery_status?: string | null;
  opened?: boolean;
  clicked?: boolean;
  // Task
  task_code?: string | null;
  // Failure
  error_log?: string | null;
}

/* ------------------------------------------------------------------ *
 * Step log (UI PRD R2 §4 — record panel / timeline)
 * GET /api/v1/sequence/get-sequence-step-log
 * ------------------------------------------------------------------ */
export interface ISequenceStepLogRow {
  enrollment_code: string;
  sequence_code?: string;
  sequence_name?: string | null;
  module_name: SequenceModule | string;
  module_code: string;
  step_order: number;
  step_name?: string | null;
  action_type: StepActionType;
  result: string | null;
  skip_reason?: string | null;
  executed_at: string | null;
  to_emails?: string[] | null;
  cc_emails?: string[] | null;
  email_history_code?: string | null;
  task_code?: string | null;
  error_log?: string | null;
}

/* ------------------------------------------------------------------ *
 * Upcoming worklist (PRD §5.6)
 * ------------------------------------------------------------------ */
export interface ISequenceUpcomingRow {
  enrollment_code: string;
  next_run_at: string;
  module_name: SequenceModule | string;
  module_code: string;
  sequence_code: string;
  sequence_name: string;
  next_step_order: number;
  action_type: StepActionType;
  step_name?: string | null;
  template_code?: string | null;
  task_name?: string | null;
}

/* ------------------------------------------------------------------ *
 * Enrol result (PRD §5.4)
 * ------------------------------------------------------------------ */
export interface IEnrolResult {
  module_code: string;
  created: boolean;
  enrollment_code?: string;
  reason?: string;
}

/* ------------------------------------------------------------------ *
 * Slice state
 * ------------------------------------------------------------------ */
export interface IEmailSequencesState {
  list: ISequenceListRow[];
  totalRecords: number;
  listLoading: LoadState;

  sequence: ISequence;
  sequenceLoading: LoadState;

  stats: ISequenceStats | null;
  statsLoading: LoadState;

  enrolments: ISequenceEnrolment[];
  enrolmentsTotalRecords: number;
  enrolmentsLoading: LoadState;

  recordSequences: ISequenceEnrolment[];
  recordSequencesLoading: LoadState;

  upcoming: ISequenceUpcomingRow[];
  upcomingTotalRecords: number;
  upcomingLoading: LoadState;
}

export const initialSequence: ISequence = {
  sequence_code: null,
  sequence_name: "",
  description: null,
  module_name: null,
  trigger_type: "MANUAL",
  trigger_template_code: null,
  contact_list_code: null,
  trigger_sql: null,
  cron_schedule_at: null,
  stop_on_reply: 1,
  stop_on_bounce: 1,
  allow_reentry: 0,
  exit_conditions: null,
  send_window: {
    days: [1, 2, 3, 4, 5],
    from: "09:00",
    to: "17:00",
    tz: "America/Toronto",
  },
  steps: [],
  status: "DRAFT",
};

export const initialEmailSequencesState: IEmailSequencesState = {
  list: [],
  totalRecords: 0,
  listLoading: LoadState.NotLoaded,

  sequence: initialSequence,
  sequenceLoading: LoadState.NotLoaded,

  stats: null,
  statsLoading: LoadState.NotLoaded,

  enrolments: [],
  enrolmentsTotalRecords: 0,
  enrolmentsLoading: LoadState.NotLoaded,

  recordSequences: [],
  recordSequencesLoading: LoadState.NotLoaded,

  upcoming: [],
  upcomingTotalRecords: 0,
  upcomingLoading: LoadState.NotLoaded,
};

/** Default scheduled-rule schedule: once a day at 08:00 (America/Toronto). */
export const defaultCronSchedule = (): ISequenceCronSchedule => ({
  second: "0",
  minute: "0",
  hour: "8",
});

/** Factory for a fresh, empty step (PRD §5.2.D defaults). */
export const createEmptyStep = (step_order: number): ISequenceStep => ({
  step_order,
  step_name: null,
  action_type: "EMAIL",
  delay_type: "AFTER_PREVIOUS",
  delay_days: 0,
  delay_hours: 0,
  anchor_column: null,
  skip_conditions: [],
  run_conditions: null,
  template_code: null,
  to_type: ["CUSTOMER"],
  to_emails: [],
  cc_emails: [],
  task_name: null,
  task_type: "Sequence",
  task_priority: null,
  assign_to_type: "CSR",
  assign_to_id: null,
  task_due_in_days: null,
  task_notes: null,
});
